import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormControl, Input } from "@hydra/atom/components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { pascalize } from "humps";
import FieldLabel from "./FieldLabel";
import { formatNumber, formatDecimalValues } from "@/utils/helpers";
import { useDebounce } from "@/hooks";
import {
  validationFieldDynamicObjectRecord,
  getFieldDynamicObjectRecordName,
  getFieldDynamicObjectArabicRecordName,
} from "@/api/dynamic/dynamicObjectNameApi";
import showToast from "@/utils/toast/helpers";

const getInputFieldType = (fieldType) => {
  switch (fieldType) {
    case "money":
    case "percentage":
    case "area":
      return "number";

    case "autonumber":
      return "text";

    default:
      return fieldType;
  }
};

const getInputFieldPlaceholder = (field, objectName) => {
  if (field.camelizedName === "bankAccount" && objectName === "Supplier") {
    return "IBAN";
  }

  const fieldType = field.objectFieldType.toLowerCase();
  switch (fieldType) {
    case "email":
      return "enter.email@here.com";

    default:
      return field.placeholder || "";
  }
};

const handleChequeNumberChange = (chequeNo) => {
  let value = chequeNo;
  const maxLength = 6;
  while (value.length < maxLength) {
    value = `0${value}`;
  }
  if (value.length > maxLength) {
    value = value.substring(0, maxLength);
  }

  return value;
};

const objectWithPersonNames = [
  "Tenant",
  "Landlord",
  "Lead",
  "Supplier",
  "Agent",
  "Customer",
];

function InputField({
  field,
  value,
  onChange,
  name,
  error,
  showLabel,
  state = {},
  disabled,
  objectName = "",
  handleDispatch = null,
  checkValidation,
  ...rest
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [validateUniqueText, setValidateUniqueText] = useState(false);
  const [personName, setPersonName] = useState("");
  const [arabicName, setArabicName] = useState("");
  const debouncedUniqueTextSearch = useDebounce(validateUniqueText, 800);
  const debouncedArabicName = useDebounce(arabicName, 300);
  const queryClient = useQueryClient();

  const { data: uniqueData } = useQuery(
    [`unique-text-${debouncedUniqueTextSearch}`],
    () =>
      validationFieldDynamicObjectRecord({
        data: debouncedUniqueTextSearch,
        objectName,
        fieldName: pascalize(field.name),
      }),
    {
      enabled: Boolean(
        debouncedUniqueTextSearch && checkValidation && field.unique
      ),
    }
  );

  const { data: itemName, isFetched: isFetchedNameApi } = useQuery(
    [`name-${personName}`],
    () =>
      getFieldDynamicObjectRecordName({
        fullName: personName,
      }),
    {
      enabled: Boolean(
        personName &&
          field.objectFieldType === "Text" &&
          objectWithPersonNames.includes(objectName)
      ),
    }
  );

  const { data: itemArabicName, isFetched: isFetchedArabicNameApi } = useQuery(
    [`arabic-${debouncedArabicName}`],
    () =>
      getFieldDynamicObjectArabicRecordName({
        fullName: debouncedArabicName,
        outputLanguage: "Arabic",
      }),
    {
      enabled: Boolean(
        debouncedArabicName &&
          field.objectFieldType === "Text" &&
          objectWithPersonNames.includes(objectName)
      ),
    }
  );

  useEffect(() => {
    if (uniqueData && handleDispatch) {
      if (uniqueData.exists) {
        handleDispatch("isUniqueText", {
          ...state.isUniqueText,
          [field.name]: false,
        });
        showToast(`${field.label} already exists`, "error");
      } else {
        handleDispatch("isUniqueText", {
          ...state.isUniqueText,
          [field.name]: true,
        });
      }
    }
  }, [uniqueData]);

  useEffect(() => {
    if (itemArabicName && handleDispatch) {
      handleDispatch("nameArabic", itemArabicName.fullName);
    }
  }, [itemArabicName, isFetchedArabicNameApi]);

  useEffect(() => {
    if (itemName && handleDispatch) {
      handleDispatch("firstName", itemName.firstName);
      handleDispatch("middleName", itemName.middleName);
      handleDispatch("lastName", itemName.lastName);
    }
  }, [itemName, isFetchedNameApi]);

  const renderAppendIcon = () => {
    if (field.currency) {
      return (
        <Input.AppendIcon>
          <span>{field.currency}</span>
        </Input.AppendIcon>
      );
    }

    if (field.objectFieldType === "Percentage") {
      return (
        <Input.AppendIcon>
          <span>%</span>
        </Input.AppendIcon>
      );
    }

    if (field.objectFieldType === "Area") {
      return (
        <Input.AppendIcon>
          <span>sq. m</span>
        </Input.AppendIcon>
      );
    }
  };

  const onBlur = (inputValue, onBlurFunc) => {
    if (field.objectFieldType === "Money") {
      setIsEditing(false);
      if (inputValue) {
        const formattedNumber = formatDecimalValues(inputValue);
        onChange(formattedNumber);
      }
    }

    if (field.camelizedName === "chequeNo") {
      setIsEditing(false);
      if (inputValue) {
        const formattedNumber = handleChequeNumberChange(inputValue);
        onChange(formattedNumber);
      }
    }

    if (onBlurFunc) onBlurFunc();
  };

  const onChangeInput = (inputValue) => {
    if (
      field.objectFieldType === "Text" &&
      objectWithPersonNames.includes(objectName)
    ) {
      if (field.name === "name") {
        setArabicName(inputValue);

        if (
          ["Individual", "Staff", "Intercompany", "FamilyRelated"].includes(
            state?.type?.value
          )
        ) {
          if (inputValue === personName) {
            return;
          }

          setPersonName(inputValue);

          queryClient.invalidateQueries({
            queryKey: [`name-${personName}`],
          });
        }
      }
    }

    let text = inputValue;

    if (field.name === "emiratesID") {
      const numbersOnly = inputValue.match(/\d+/g);
      const concatenatedNumbers = numbersOnly?.join("");
      text = concatenatedNumbers;
    }

    if (field.unique) {
      if (text) {
        if (text === validateUniqueText && !state.isUniqueText[field.name]) {
          showToast(`${field.label} already exists`, "error");
        } else {
          setValidateUniqueText(text);
          queryClient.invalidateQueries({
            queryKey: [`unique-text-${text}`],
          });
        }
      }
    }

    onChange(text);
  };

  const renderInputField = () => {
    switch (field.objectFieldType.toLowerCase()) {
      case "money":
        return isEditing ? (
          <Input
            id={name}
            type={getInputFieldType(field.objectFieldType.toLowerCase())}
            value={value}
            onChange={onChange}
            disabled={field.disabled || disabled}
            onWheel={(e) => e.target.blur()}
            step="0.01"
            {...rest}
            onBlur={() => onBlur(value, rest.onBlur)}
          >
            {renderAppendIcon()}
          </Input>
        ) : (
          <Input
            id={name}
            type="text"
            value={value ? formatNumber(value) : ""}
            onFocus={() => setIsEditing(true)}
            disabled={field.disabled || disabled}
            {...rest}
            onBlur={() => onBlur(value, rest.onBlur)}
          >
            {renderAppendIcon()}
          </Input>
        );

      default:
        return (
          <Input
            id={name}
            type={getInputFieldType(field.objectFieldType.toLowerCase())}
            value={value}
            onChange={(e) => onChangeInput(e)}
            disabled={field.disabled || disabled}
            onWheel={(e) => e.target.blur()}
            dir={field.direction || "ltr"}
            placeholder={getInputFieldPlaceholder(field, objectName)}
            {...rest}
            onBlur={() => onBlur(value, rest.onBlur)}
          >
            {renderAppendIcon()}
          </Input>
        );
    }
  };

  return (
    <>
      <FormControl>
        {showLabel ? (
          <FieldLabel
            label={field.label}
            labelFor={field.name}
            helperText={field.helperText}
            isRequired={field.required}
          />
        ) : null}
        {renderInputField()}
      </FormControl>
      {typeof error === "string" && <span className="error-text">{error}</span>}
    </>
  );
}

InputField.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  name: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  showLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  checkValidation: PropTypes.bool,
};

InputField.defaultProps = {
  value: "",
  onChange: () => {},
  name: "tuli-input-field",
  error: false,
  showLabel: true,
  disabled: false,
  checkValidation: true,
};

InputField.Type = "HydraValidatableComponent";

export default InputField;
